<template>
  <!-- <transition name="fade"> -->
  <nav :class="{ active: active }">
    <div class="logo" @click="onClickLogo">
      <svg-icon :width="'24'" :height="'24'" icon="app-icons/logo" />
    </div>
    <div class="project_name">
      {{ getName }}
    </div>
    <div class="tools">
      <div
        v-if="getAudioReferencesExist"
        ref="audioButton"
        class="audio-handler"
        :class="{ grey: showVolume }"
      >
        <div class="volume-bars" @click="toggleAudioSettings">
          <svg-icon
            v-tooltip="audioTooltipOptions"
            :class="volumeClass"
            width="20"
            height="20"
            icon="app-icons/volume"
          />
        </div>
        <div
          class="audio-volume grey"
          :class="{ hidden: !showVolume }"
          :style="{
            '--volume-before-width': `${parseInt(volume * 100, 10)}px`,
          }"
        >
          <div @click="toggleMute">
            <svg-icon
              fill="#bcbcbc"
              width="20"
              height="20"
              :icon="
                parseFloat(volume) === 0
                  ? 'app-icons/audio-start'
                  : 'app-icons/audio-stop'
              "
            />
          </div>
          <input
            v-model="volume"
            type="range"
            max="1"
            step="0.01"
            @input="onVolumeInput"
            @change="onVolumeChange"
          />
          <div class="volume-text">{{ parseInt(volume * 100, 10) }}</div>
        </div>
      </div>
      <Protect
        :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
        :roles="['editor']"
      >
        <span
          class="debug"
          :class="{ grey: getDebugSection === true }"
          @click="openDebug()"
        >
          <svg-icon
            v-tooltip="debugTooltipOptions"
            :fill="'#db841e'"
            :width="'20'"
            :height="'20'"
            icon="app-icons/debug"
          />
        </span>
      </Protect>
      <profile-menu v-if="getIsProjectLoaded" />
    </div>
  </nav>
  <!-- </transition> -->
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import Protect from '@/hoc/Protect.vue';
import { SYS_PERMISSIONS } from '@/constants.js';
import mixpanelSingleton from '@/mixpanel/mixpanelSingleton.js';
import ProfileMenu from '../board/ProfileMenu.vue';
import SvgIcon from '../all/SvgIcon.vue';

export default {
  components: {
    SvgIcon,
    'profile-menu': ProfileMenu,
    Protect,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    audioError: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      audioTooltipOptions: {
        placement: 'bottom',
        content: 'Audio',
        distance: 5,
        popperClass: ['secondary'],
      },
      debugTooltipOptions: {
        placement: 'bottom',
        content: 'Debug',
        distance: 5,
        popperClass: ['secondary'],
      },
      delayOptions: { show: 100, hide: 100 },
      showVolume: false,
      volume: 1,
      lastVolume: 1,
      SYS_PERMISSIONS,
    };
  },

  computed: {
    ...mapGetters('user/account', ['getIsLoggedIn']),
    ...mapGetters(['getWriteAuthorization']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),
    ...mapGetters('project/config', ['getGrid']),
    ...mapGetters('project', ['getName', 'getIsUnsaved']),
    ...mapGetters('application', ['getDebugSection']),
    ...mapGetters('project/data/elements', ['getAudioReferencesExist']),

    volumeClass() {
      const val = parseFloat(this.volume);
      if (val > 0.75) {
        return '';
      }
      if (val > 0.5) {
        return 'third';
      }
      if (val > 0.25) {
        return 'second';
      }
      if (val > 0) {
        return 'first';
      }

      return 'muted';
    },
  },

  watch: {
    active(newValue, oldValue) {
      if (!newValue) {
        this.showVolume = false;
      }
    },

    audioError(newValue, oldValue) {},
  },

  created() {},

  methods: {
    ...mapMutations('application', ['setDebugSection']),
    ...mapMutations('playMode', ['setAudioLevel']),

    /**
     * Event listener - user clicks on the logo. Redirects to the homepage.
     */
    onClickLogo() {
      if (this.getIsLoggedIn) {
        this.$router.push({ name: 'workspace' });
      } else {
        window.location.href = '/';
      }
    },

    openDebug() {
      this.setDebugSection({ value: !this.getDebugSection });
      if (this.getDebugSection) {
        // Mix Panel Event
        mixpanelSingleton.track('button_clicked', { type: 'debugger' });
      }
    },

    toggleAudioSettings() {
      this.showVolume = !this.showVolume;
    },

    toggleMute() {
      this.volume = parseFloat(this.volume);
      if (this.volume > 0) {
        this.lastVolume = this.volume;
        this.volume = 0;
      } else {
        this.volume = this.lastVolume;
      }

      this.setAudioLevel({ level: this.volume });
    },

    onVolumeInput() {
      this.volume = parseFloat(this.volume);
      this.setAudioLevel({ level: this.volume });
    },

    onVolumeChange(event) {
      if (this.volume > 0) {
        this.lastVolume = this.volume;
      }
    },
  },
};
</script>

<style lang="scss">
.tooltip.popover .popover-inner.send-popover-inner {
  background: #2c3233;
  color: #bcbcbc;
  border-radius: 3px;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
  padding: 0;
  overflow: hidden;
  .button:hover {
    background: #3a4143;
    cursor: pointer;
  }
  .button {
    text-align: left;
    padding: 12px 20px;
    font-size: 13px;
    color: inherit;
    text-decoration: none;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    span {
      flex: 1;
    }
    svg {
      margin: 0 10px 0 0;
    }
  }
}
.grey {
  background-color: #2c3233;
}
.tools .audio-handler .volume-bars {
  svg path:nth-child(1) {
    opacity: 50%;
  }
  svg path:nth-child(2) {
    opacity: 70%;
  }
  svg path:nth-child(3) {
    opacity: 75%;
  }
  .muted {
    svg path {
      opacity: 25%;
    }
  }

  .first {
    svg path:nth-child(2),
    svg path:nth-child(3),
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }

  .second {
    svg path:nth-child(3),
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }

  .third {
    svg path:nth-child(4) {
      opacity: 25%;
    }
  }
}
</style>
<style scoped lang="scss">
nav {
  display: flex;
  align-items: center;
  background: #1e2121;
  width: 100%;
  min-height: 40px;
  // opacity: 0;
  position: fixed;
  z-index: 2;
  // transition: opacity 0.5s ease 2s;
  &:hover {
    opacity: 1;
    // transition: opacity 0.5s ease 0s;
  }

  .profile {
    margin: 0 15px 0 15px;
  }
  .logo {
    display: flex;
    flex: 1;
    justify-self: flex-start;

    & > div {
      cursor: pointer;
      margin-left: 12px;
    }
  }
  .project_name {
    display: flex;
    flex: 1;
    color: #bcbcbc;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    cursor: default;
    user-select: none;
  }

  .tools {
    display: flex;
    flex: 1;
    justify-content: flex-end;
    align-items: center;

    .svg-icon-container {
      cursor: pointer;
      width: 40px;
      height: 40px;
    }
    .audio-volume {
      position: absolute;
      display: flex;
      align-items: center;
      height: 40px;
      z-index: 3;
      transform: translateX(calc(40px - 100%));

      &.hidden {
        display: none;
      }

      input[type='range'] {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        cursor: pointer;
        width: 100px;
      }

      /***** Track Styles *****/
      /***** Chrome, Safari, Opera, and Edge Chromium *****/
      input[type='range']::-webkit-slider-runnable-track {
        background: #1e2121;
        height: 8px;
        border-radius: 8px;
      }

      input[type='range']::-webkit-slider-thumb {
        -webkit-appearance: none; /* Override default look */
        appearance: none;
        margin-top: 0; /* Centers thumb on the track */
        background-color: transparent;
        height: 8px;
        width: 8px;
        border-radius: 50%;
      }

      input[type='range']::before {
        width: var(--volume-before-width);
        background: #ffffff;
        position: absolute;
        content: '';
        height: 8px;
        border-radius: 8px;
      }

      /******** Firefox ********/
      input[type='range']::-moz-range-track {
        background: #1e2121;
        height: 8px;
        border-radius: 8px;
      }

      input[type='range']::-moz-range-thumb {
        border: none; /*Removes extra border that FF applies*/
        border-radius: 50%; /*Removes default border-radius that FF applies*/
        background-color: transparent;
        height: 8px;
        width: 8px;
      }

      input[type='range']::-moz-range-progress {
        background: #ffffff;
        height: 8px;
        border-radius: 8px;
      }

      .svg-icon-container {
        cursor: pointer;
      }

      .volume-text {
        color: #bcbcbc;
        width: 34px;
        text-align: center;
      }
    }
  }

  .project-list {
    color: #db841e;
    padding: 10px 15px;
    background: none;
    border: none;
    cursor: pointer;
    user-select: none;
  }

  .divider {
    width: 1px;
    height: 40px;
    background: #4a5254;
    flex-shrink: 0;
    min-width: 1px;
  }
  .button {
    padding: 6px 10px;
    background: #db841e;
    color: #fff;
    border-radius: 3px;
    cursor: pointer;
    text-decoration: none;
  }
  .share {
    margin: 0 15px 0;
    cursor: pointer;
  }
  // .settings_btn {
  //   margin: 0 10px 0 0;
  //   cursor: pointer;
  // }
  // .export_btn {
  //   border: none;
  //   border-radius: 4px;
  //   cursor: pointer;
  //   color: #db841e;
  //   padding: 3px 12px 7px;
  //   background: rgba(45, 50, 51, 1);
  //   margin: 0 10px 0 0;
  //   min-width: 30px;
  //   flex-shrink: 0;
  //   svg {
  //     path {
  //       fill: #ffffff;
  //     }
  //   }
  //   &:hover {
  //     color: #fff;
  //   }
  // }
}
</style>
